import { Tooltip } from '@mui/material';
import { dateFormatter, limitDecimals, lrpDateFormat, pages } from 'common';
import {
  isRmaLrpTypeCode,
  getLrpCommodityInfo,
  LgmCommodityInfo,
  InsureIQPolicyTypes,
  RmaLrpCommodityNameByCode,
  RmaLgmCommodityNameByCode,
  isRmaLrpCommodityCode,
  isRmaLgmCommodityCode,
  isRmaLgmTypeCode,
} from '@harvestiq/constants';
import { dayjs, formatCurrency, formatNumber } from '@harvestiq/utils';
import { getEntityName } from '@harvestiq/iiq-common';
import { EndorsementStatusChip } from '../components/EndorsementStatusChip/EndorsementStatusChip';
import { InfoTooltip } from '../components/InfoTooltip';

const tooltipTitleMap = {
  // First index is expected/projected and second index is actual
  endingValue: ['Expected Ending Value', 'Actual Ending Value'],
  indemnity: ['Projected Indemnity', 'Actual Indemnity'],
  grossMargin: ['Projected Gross Margin', 'Actual Gross Margin'],
  grossMarginChange: [
    'Projected Gross Margin Change',
    'Actual Gross Margin Change',
  ],
};

export const getToolTipTitle = (key, value) => {
  const isActual = value !== null && value !== undefined;
  if (isActual) {
    return tooltipTitleMap[key]?.[1]; // index 1
  }
  return tooltipTitleMap[key]?.[0]; // index 0
};

export const calculationTypes = {
  cwt: 'cwt',
  head: 'head',
  total: 'total',
};

export const priceToggleOptions = {
  cash: 'cash',
  futures: 'futures',
};
const projectedIndemnityTooltipTitle =
  'Projected Indemnity is estimated given current market conditions and does not guarantee a specific outcome.';

const lgmCoverageSubTableColumns = [
  { field: 'marketingMonth', label: 'Marketing Month' },
  { field: 'numberofHead', label: 'Number of Head' },
  {
    field: 'premium',
    label: 'Producer Premium',
    render: (monthPremium) => formatCurrency(monthPremium),
  },
  {
    field: 'grossMarginGuarantee',
    label: 'Gross Margin Guarantee',
    render: (monthGrossMarginGuarantee) =>
      formatCurrency(monthGrossMarginGuarantee),
  },
  {
    field: 'grossMargin',
    label: 'Gross Margin',
    render: (monthGrossMargin, { actualGrossMargin }) => (
      <Tooltip title={getToolTipTitle('grossMargin', actualGrossMargin)}>
        <span>{formatCurrency(monthGrossMargin)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'grossMarginChange',
    label: (
      <InfoTooltip
        title={
          'Gross Margin Change is estimated given current market conditions and does not guarantee a specific outcome.'
        }
      >
        Gross Margin Change
      </InfoTooltip>
    ),
    render: (monthGrossMarginChange, { actualGrossMarginChange }) => {
      // NOTE: The math behind gross margin change is correct, the sign is correct calculation wise, but for ensuring better UX we are inversing the signal.
      const adjustedSignalMonthGrossMarginChange = monthGrossMarginChange * -1;

      return (
        <Tooltip
          title={getToolTipTitle('grossMarginChange', actualGrossMarginChange)}
        >
          <span>
            {formatCurrency(adjustedSignalMonthGrossMarginChange, {
              signDisplay: 'always',
            })}
          </span>
        </Tooltip>
      );
    },
  },
];

export const calculationOptionsByPolicyType = {
  [InsureIQPolicyTypes.LRP]: [
    calculationTypes.cwt,
    calculationTypes.head,
    calculationTypes.total,
  ],
  [InsureIQPolicyTypes.LGM]: [calculationTypes.head, calculationTypes.total],
};

export const calculatorsByCalculationType = {
  cwt: () => (value) => limitDecimals(value),

  head: (targetWeight = 1) => {
    return (value) => {
      return limitDecimals(value * targetWeight);
    };
  },

  total: (targetWeight, numberOfHeads) => {
    return (value) => {
      return limitDecimals(numberOfHeads * targetWeight * value);
    };
  },
};

const calculateMarketingMonthValues = (marketingMonth, calculationType) => {
  const {
    subsidy,
    premium,
    actualIndemnity,
    projectedIndemnity,
    actualGrossMargin,
    projectedGrossMargin,
    numberofHead,
    grossMarginGuarantee,
    actualGrossMarginChange,
    projectedGrossMarginChange,
  } = marketingMonth;

  const calculator = calculatorsByCalculationType[calculationType](
    1,
    numberofHead
  );

  return {
    ...marketingMonth,
    subsidy: calculator(subsidy),
    premium: calculator(premium),
    // Ensure that indemnity is never displayed as a negative value on a monthly basis
    indemnity: Math.max(0, calculator(actualIndemnity ?? projectedIndemnity)),
    grossMarginChange: calculator(
      actualGrossMarginChange ?? projectedGrossMarginChange
    ),
    grossMargin: calculator(actualGrossMargin ?? projectedGrossMargin),
    grossMarginGuarantee: calculator(grossMarginGuarantee),
  };
};

const calculateLgmValues = (coverage, calculationType) => {
  const { deductible } = coverage;
  const marketingMonths =
    coverage.lgmIndemnityDetails?.marketingMonths ??
    coverage.details.marketingMonths; // fallback to details.marketingMonths when lgmIndemnityDetails is not available

  return {
    deductible,
    numberofHead: coverage.targetMarketingsTotal,
  };
};

export const restructureCoverages = (
  coverages = [],
  calculationType = calculationTypes.head
) => {
  return coverages.map((rawCoverage) => {
    const coverage = { ...rawCoverage };

    if (coverage.type === InsureIQPolicyTypes.LGM) {
      Object.assign(coverage, calculateLgmValues(coverage, calculationType));
    } else {
      let coverageValuesUom = {};
      switch (calculationType) {
        case calculationTypes.cwt:
          coverageValuesUom = {
            premium: coverage.producerPremiumPerCwt,
            cashIndemnity: coverage.projectedCashIndemnityPerCwt,
            futuresIndemnity: coverage.projectedFuturesIndemnityPerCwt,
            actualIndemnity: coverage.actualIndemnityPerCwt,
            endingValue:
              coverage.actualEndingValuePerCwt ??
              coverage.expectedEndingValuePerCwt,
            cashPrice: coverage.projectedCashEndingValuePerCwt,
            futuresPrice: coverage.projectedFuturesEndingValuePerCwt,
            actualEndingValue: coverage.actualEndingValuePerCwt,
            coveragePrice: coverage.coveragePricePerCwt,
          };
          break;
        case calculationTypes.head:
          coverageValuesUom = {
            premium: coverage.producerPremiumPerHead,
            cashIndemnity: coverage.projectedCashIndemnityPerHead,
            futuresIndemnity: coverage.projectedFuturesIndemnityPerHead,
            actualIndemnity: coverage.actualIndemnityPerHead,
            endingValue:
              coverage.actualEndingValuePerHead ??
              coverage.expectedEndingValuePerHead,
            cashPrice: coverage.projectedCashEndingValuePerHead,
            futuresPrice: coverage.projectedFuturesEndingValuePerHead,
            actualEndingValue: coverage.actualEndingValuePerHead,
            coveragePrice: coverage.coveragePricePerHead,
          };
          break;

        default:
          coverageValuesUom = {
            premium: coverage.producerPremiumTotal,
            cashIndemnity: coverage.projectedCashIndemnityTotal,
            futuresIndemnity: coverage.projectedFuturesIndemnityTotal,
            actualIndemnity: coverage.actualIndemnityTotal,
            endingValue:
              coverage.actualEndingValueTotal ??
              coverage.expectedEndingValueTotal,
            cashPrice: coverage.projectedCashEndingValueTotal,
            futuresPrice: coverage.projectedFuturesEndingValueTotal,
            actualEndingValue: coverage.actualEndingValueTotal,
            coveragePrice: coverage.coveragePriceTotal,
          };
          break;
      }

      Object.assign(coverage, {
        ...coverageValuesUom,
        numberofHead: coverage.headCount,
      });
    }

    return { ...coverage };
  });
};

export const statusColumn = {
  id: 'status',
  label: 'Status',
  isSortable: true,
  render: (status, { externalStatus, isExternallyManaged }) => {
    return (
      <EndorsementStatusChip
        status={status}
        isExternallyManaged={isExternallyManaged}
        externalStatus={externalStatus}
      />
    );
  },
};

export const ownersColumn = {
  id: 'owner',
  label: 'Entity Name',
  render: (_, { entityHistory }) => {
    return getEntityName(entityHistory);
  },
};

export const commodityColumn = {
  id: 'commodityCode',
  label: 'Commodity',
  isSortable: true,
  render: (commodityCode, { typeCode }) => {
    if (isRmaLrpCommodityCode(commodityCode) && isRmaLrpTypeCode(typeCode)) {
      const commodityType = getLrpCommodityInfo({
        commodityCode,
        typeCode,
      }).hiqTypeCodeName;
      const commodityName = RmaLrpCommodityNameByCode[commodityCode];
      return `${commodityName} - ${commodityType}`;
    }

    if (isRmaLgmCommodityCode(commodityCode) && isRmaLgmTypeCode(typeCode)) {
      const commodityType = LgmCommodityInfo[typeCode].hiqTypeCodeName;
      const commodityName = RmaLgmCommodityNameByCode[commodityCode];
      return `${commodityName} - ${commodityType}`;
    }

    return '';
  },
};

export const salesEffectiveDateColumn = {
  id: 'salesEffectiveDate',
  label: 'Sales Effective Date',
  render: (salesEffectiveDate) =>
    dateFormatter(salesEffectiveDate, lrpDateFormat),
};

export const endDateColumn = {
  id: 'endDate',
  label: (
    <InfoTooltip title="Specific Coverage Endorsement End Date indicates when the given LRP Position will close.">
      SCE End Date
    </InfoTooltip>
  ),
};

export const deductibleColumn = {
  id: 'deductible',
  label: 'Deductible',
  render: (deductible) => formatCurrency(deductible),
};

export const targetWeightColumn = {
  id: 'targetWeightQuantityCwt',
  label: 'Target Weight (cwt)',
  render: (targetWeightQuantityCwt) => {
    return targetWeightQuantityCwt ? targetWeightQuantityCwt.toNumber() : '-';
  },
};

export const headCountColumn = {
  id: 'numberofHead',
  label: 'Total Number of Head',
  render: (headCount) => (headCount ? formatNumber(headCount, 0) : '-'),
};

export const premiumColumn = {
  id: 'premium',
  label: 'Premium',
  render: (premium) => formatCurrency(premium),
};

export const coveragePriceColumn = {
  id: 'coveragePrice',
  label: 'Coverage Price',
  render: (coveragePrice) => {
    return coveragePrice ? formatCurrency(coveragePrice) : '-';
  },
};

export const endingValueColumn = {
  id: 'endingValue',
  label: (
    <InfoTooltip
      title={
        <>
          Latest relevant cash value. See the{' '}
          <a
            href={`${pages.faq.path}?faqs=what-are-the-calculations-for-actual-ending-value-and-expected-ending-value`}
            target="_blank"
            rel="noreferrer"
          >
            FAQ
          </a>{' '}
          for more detail.
        </>
      }
    >
      Cash Price
    </InfoTooltip>
  ),
  render: (endingValue, row) => {
    return <span>{formatCurrency(endingValue)}</span>;
  },
};

export const generateCashPriceTooltip = ({
  cashSymbol,
  cashTimestamp,
  commodity,
}) => {
  if (!cashSymbol || !commodity) {
    return '';
  }

  const textLine1 = `CME ${commodity || ''} Cash (${cashSymbol})`;
  const textLine2 = cashTimestamp
    ? `${dayjs(cashTimestamp).format('YYYY-MM-DD')} Close Price`
    : '';
  return (
    <>
      {textLine1} <br />
      {textLine2}
    </>
  );
};

export const generateFuturesPriceTooltip = ({
  commodity,
  futuresSymbol,
  futuresTimestamp,
}) => {
  if (!commodity || !futuresSymbol) {
    return '';
  }

  const textLine1 = `CME ${commodity || ''} Futures (${futuresSymbol})`;
  const textLine2 = futuresTimestamp
    ? `${dayjs(futuresTimestamp).format('YYYY-MM-DD')} Close Price`
    : '';
  return (
    <>
      {textLine1} <br />
      {textLine2}
    </>
  );
};

export const cashPriceColumn = {
  id: 'cashPrice',
  label: (
    <InfoTooltip
      title={
        <>
          Latest relevant cash value. See the{' '}
          <a
            href={`${pages.faq.path}?faqs=what-are-the-calculations-for-actual-ending-value-and-expected-ending-value`}
            target="_blank"
            rel="noreferrer"
          >
            FAQ
          </a>{' '}
          for more detail.
        </>
      }
    >
      Cash Price
    </InfoTooltip>
  ),
  render: (
    cashPrice,
    { actualEndingValueTotal, cashSymbol, cashTimestamp, commodity }
  ) => {
    if (!cashPrice || actualEndingValueTotal) {
      return '-';
    }
    return (
      <Tooltip
        title={generateCashPriceTooltip({
          cashSymbol,
          cashTimestamp,
          commodity,
        })}
      >
        <span>{formatCurrency(cashPrice)}</span>
      </Tooltip>
    );
  },
};

export const futuresPriceColumn = {
  id: 'futuresPrice',
  label: 'Futures Price',
  render: (
    futuresPrice,
    { actualEndingValueTotal, commodity, futuresSymbol, futuresTimestamp }
  ) => {
    if (!futuresPrice || actualEndingValueTotal) {
      return '-';
    }
    return (
      <Tooltip
        title={generateFuturesPriceTooltip({
          commodity,
          futuresSymbol,
          futuresTimestamp,
        })}
      >
        <span>{formatCurrency(futuresPrice)}</span>
      </Tooltip>
    );
  },
};

export const actualEndingValueColumn = {
  id: 'actualEndingValue',
  label: 'Actual Ending Value',
  render: (actualEndingValue) => {
    return actualEndingValue ? formatCurrency(actualEndingValue) : '-';
  },
};

export const cashIndemnityColumn = {
  id: 'cashIndemnity',
  label: (
    <InfoTooltip title={projectedIndemnityTooltipTitle}>
      Projected Indemnity
    </InfoTooltip>
  ),
  render: (indemnity, coverage) => {
    const projectedIndemnity =
      coverage.actualIndemnity ?? coverage.cashIndemnity;
    return <span>{formatCurrency(projectedIndemnity)}</span>;
  },
};

export const futuresIndemnityColumn = {
  id: 'futuresIndemnity',
  label: (
    <InfoTooltip title={projectedIndemnityTooltipTitle}>
      Projected Indemnity
    </InfoTooltip>
  ),
  render: (indemnity, coverage) => {
    const projectedIndemnity =
      coverage.actualIndemnity ?? coverage.futuresIndemnity;
    return <span>{formatCurrency(projectedIndemnity)}</span>;
  },
};

export const lgmProjectedOrActualIndemnityColumn = {
  id: 'lgmProjectIndemnity',
  label: (
    <InfoTooltip title={projectedIndemnityTooltipTitle}>
      Projected Indemnity
    </InfoTooltip>
  ),
  render: (_, { lgmProjectedIndemnity, lgmActualIndemnity }) => {
    return (
      <span>{formatCurrency(lgmActualIndemnity ?? lgmProjectedIndemnity)}</span>
    );
  },
};
